import React, { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';

const ApiStatusIndicator = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api-status');
      setStatus(res.data);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Error checking API status:', error);
      setStatus({
        sources: {
          piste: { connected: false, name: "PISTE", icon: "⚖️" },
          cedh: { connected: false, name: "CEDH", icon: "🇪🇺" },
          eurlex: { connected: false, name: "EUR-Lex", icon: "🌍" },
          conseil_etat: { connected: false, name: "Conseil d'État", icon: "🏛️" },
          conseil_constit: { connected: false, name: "Cons. Constit.", icon: "📜" },
        },
        summary: { connected: 0, total: 5, all_connected: false }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!status) return null;

  const { sources, summary } = status;
  const allConnected = summary?.all_connected;
  const someConnected = summary?.connected > 0;

  return (
    <div className="px-2 py-2 border-t border-[#B69D74]/10">
      {/* Header - clickable to expand */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-2 hover:bg-white/5 transition-colors duration-200"
      >
        <div className="flex items-center gap-2">
          {/* Status indicator dot */}
          <div className={cn(
            "w-2 h-2",
            allConnected ? "bg-emerald-400" : someConnected ? "bg-amber-400" : "bg-red-400",
            loading && "animate-pulse"
          )} />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">
            Sources
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className={cn(
            "text-xs font-mono",
            allConnected ? "text-emerald-400" : someConnected ? "text-amber-400" : "text-red-400"
          )}>
            {summary?.connected}/{summary?.total}
          </span>
          {expanded ? (
            <ChevronUp className="w-3 h-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded list */}
      {expanded && (
        <div className="mt-1 space-y-0.5 animate-fade-in">
          {Object.entries(sources).map(([key, source]) => (
            <div 
              key={key}
              className="flex items-center justify-between px-2 py-1.5 text-xs"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs">{source.icon}</span>
                <span className="text-muted-foreground">{source.name}</span>
              </div>
              {source.connected ? (
                <CheckCircle className="w-3 h-3 text-emerald-400" />
              ) : (
                <XCircle className="w-3 h-3 text-red-400" />
              )}
            </div>
          ))}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                checkStatus();
              }}
              disabled={loading}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#66FCF1] transition-colors duration-200"
            >
              <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
              <span>Actualiser</span>
            </button>
            
            {!allConnected && (
              <Link
                to="/settings"
                onClick={() => setExpanded(false)}
                className="flex items-center gap-1 text-xs text-[#B69D74] hover:underline"
              >
                <Settings className="w-3 h-3" />
                <span>Configurer</span>
              </Link>
            )}
          </div>

          {/* Last check time */}
          {lastCheck && (
            <p className="text-[10px] text-[#94A3B8]/50 text-center pt-1 font-mono">
              {lastCheck.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiStatusIndicator;
